import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
/* -------------------------------------------------------------------------- */
/* ROUTE                                                                      */
/* -------------------------------------------------------------------------- */
export const Route = createFileRoute('/_layout/faq/')({
    component: FAQPage,
    head: () => ({
        meta: [
            { title: 'FAQ — Adora Analytics Questions Answered' },
            {
                name: 'description',
                content: 'Answers to common questions about Adora campaigns, Smart Links, QR tracking, pricing, and analytics. Start free, no credit card needed.',
            },
            { property: 'og:title', content: 'FAQ — Adora Analytics Questions Answered' },
            {
                property: 'og:description',
                content: 'Answers to common questions about Adora campaigns, Smart Links, QR tracking, pricing, and analytics. Start free, no credit card needed.',
            },
        ],
    }),
});
/* -------------------------------------------------------------------------- */
/* ANIMATION                                                                  */
/* -------------------------------------------------------------------------- */
const fadeUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
};
/* -------------------------------------------------------------------------- */
/* SCHEMA.ORG FAQ JSON-LD                                                     */
/* -------------------------------------------------------------------------- */
function FAQSchema({ faq }) {
    const questions = Object.values(faq).flatMap((group) => Object.values(group.items).map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer,
        },
    })));
    const schema = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: questions,
    };
    return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}/>;
}
/* -------------------------------------------------------------------------- */
/* PAGE                                                                       */
/* -------------------------------------------------------------------------- */
function FAQPage() {
    const { t } = useTranslation('faq');
    const faq = t('faq', { returnObjects: true });
    return (<React.Fragment>
            {/* SEO / Google Rich Results */}
            <FAQSchema faq={faq}/>

            <motion.section className="min-h-screen bg-background" initial="hidden" animate="visible">
                <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
                    {/* HEADER */}
                    <motion.div className="mb-10 text-center space-y-2" variants={fadeUp}>
                        <h1 className="text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight">
                            {t('faqPage.title', 'Frequently asked questions')}
                        </h1>
                        <p className="text-sm sm:text-base text-muted-foreground">
                            {t('faqPage.subtitle', 'Clear answers about Adora campaigns, pricing, and analytics.')}
                        </p>
                    </motion.div>

                    {/* GROUPED FAQ */}
                    <div className="space-y-10">
                        {Object.entries(faq).map(([groupKey, group]) => (<motion.div key={groupKey} variants={fadeUp}>
                                <h2 className="mb-4 text-lg font-semibold tracking-tight">{group.title}</h2>

                                <Accordion type="single" collapsible className="w-full">
                                    {Object.entries(group.items).map(([key, item]) => (<AccordionItem value={key} key={key}>
                                            <AccordionTrigger className="text-left">{item.question}</AccordionTrigger>
                                            <AccordionContent className="text-sm text-muted-foreground leading-relaxed">
                                                {item.answer}
                                            </AccordionContent>
                                        </AccordionItem>))}
                                </Accordion>
                            </motion.div>))}
                    </div>

                    {/* FOOTER NOTE */}
                    <motion.p className="mt-10 text-center text-xs text-muted-foreground" variants={fadeUp}>
                        {t('faqPage.footerNote', 'Still unsure? You can start free and explore campaigns before upgrading.')}
                    </motion.p>
                </div>
            </motion.section>
        </React.Fragment>);
}
